/**
 * Iter 193 — Puter.ai service wrapper.
 *
 * Thin client over `window.puter.ai.chat` that:
 *   1. Waits for puter.js to be ready (loaded via a <script> tag in index.html).
 *   2. Normalises every call to our internal JSON schema
 *      `{direction, confidence, narrative, bullets, support, resistance,
 *       levels, trendlines, patterns}` so the Chartick Suite + Chart Scanner
 *      UI render exactly like they did on the Emergent backend.
 *   3. Falls back gracefully if the user hasn't signed into their Puter
 *      account yet — surfaces a clear instruction instead of a stack trace.
 *
 * The Puter model is user-pays: the first `chat` call opens a sign-in
 * popup once per browser session; subsequent calls bill the user's own
 * Puter wallet. **Zero server-side keys.**
 */

const VISION_MODEL_DEFAULT = "gpt-5.6-luna";

/* ──────────────────────────────────────────────────────────────────────────
 * puter.js readiness
 * ────────────────────────────────────────────────────────────────────────── */
export function hasPuter() {
  return typeof window !== "undefined"
    && !!window.puter
    && !!window.puter.ai
    && typeof window.puter.ai.chat === "function";
}

export function waitForPuter(timeoutMs = 6000) {
  return new Promise((resolve, reject) => {
    if (hasPuter()) { resolve(window.puter); return; }
    const t0 = Date.now();
    const tick = setInterval(() => {
      if (hasPuter()) { clearInterval(tick); resolve(window.puter); return; }
      if (Date.now() - t0 > timeoutMs) {
        clearInterval(tick);
        reject(new Error("puter.js did not load — check your network / ad-blocker"));
      }
    }, 120);
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * JSON parsing helpers
 * ────────────────────────────────────────────────────────────────────────── */
function _extractJson(text) {
  if (!text || typeof text !== "string") return null;
  // Strip ```json ... ``` fences first.
  const fenced = text.match(/```(?:json)?\s*([\s\S]+?)```/i);
  const candidate = fenced ? fenced[1] : text;
  // Find the outermost {} span.
  const start = candidate.indexOf("{");
  const end   = candidate.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  const slice = candidate.slice(start, end + 1);
  try { return JSON.parse(slice); } catch (_e) { /* fall through */ }
  // One soft fix: trailing commas.
  try { return JSON.parse(slice.replace(/,\s*([}\]])/g, "$1")); }
  catch (_e) { return null; }
}

function _normalise(obj) {
  if (!obj || typeof obj !== "object") obj = {};
  const dir = String(obj.direction || obj.signal || "NONE").toUpperCase();
  const safeDir = dir === "BUY" ? "CALL"
                : dir === "SELL" ? "PUT"
                : (dir === "CALL" || dir === "PUT" ? dir : "NONE");
  const num = (x, d = 0) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : d;
  };
  return {
    direction: safeDir,
    confidence: Math.max(0, Math.min(1, num(obj.confidence, 0))),
    narrative: String(obj.narrative || obj.summary || "").slice(0, 1200),
    bullets: Array.isArray(obj.bullets) ? obj.bullets.map(String).slice(0, 6) : [],
    support:    Array.isArray(obj.support)    ? obj.support.map(num).slice(0, 5)    : [],
    resistance: Array.isArray(obj.resistance) ? obj.resistance.map(num).slice(0, 5) : [],
    levels:     Array.isArray(obj.levels)     ? obj.levels.filter((l) => l && typeof l === "object").slice(0, 10) : [],
    trendlines: Array.isArray(obj.trendlines) ? obj.trendlines.filter((t) => t && typeof t === "object").slice(0, 6) : [],
    patterns:   Array.isArray(obj.patterns)   ? obj.patterns.map(String).slice(0, 6) : [],
    vision_used: true,
    model: obj.__model || VISION_MODEL_DEFAULT,
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Vision prompt — matches the schema the UI already consumes
 * ────────────────────────────────────────────────────────────────────────── */
function _visionPrompt(assetHint, tfHint) {
  const a = assetHint ? `Asset hint: ${assetHint}. ` : "";
  const t = tfHint ? `Timeframe hint: ${tfHint}. ` : "";
  return [
    "You are a professional short-horizon chart technician.",
    `${a}${t}Analyse the attached chart image.`,
    "Return STRICT JSON only — no prose outside the braces, no markdown fences. Schema:",
    `{
      "direction": "CALL" | "PUT" | "NONE",
      "confidence": 0.0..1.0,
      "narrative": "two-sentence summary of what the chart says",
      "bullets": ["3-5 single-sentence observations"],
      "support": [numeric price levels, 2-3 items],
      "resistance": [numeric price levels, 2-3 items],
      "levels": [{"kind":"S"|"R","price":<num>,"y":0..1}],
      "trendlines": [{"x1":0..1,"y1":0..1,"x2":0..1,"y2":0..1,"slope":<num>}],
      "patterns": ["doji", "engulfing", "bull-flag", ...]
    }`,
    "Levels and trendline coordinates are NORMALISED to the image dimensions (0 top-left, 1 bottom-right).",
    "Pick CALL only if the next 1-3 bars look bullish on this timeframe, PUT only if clearly bearish, else NONE.",
  ].join("\n");
}

/* ──────────────────────────────────────────────────────────────────────────
 * Public API
 * ────────────────────────────────────────────────────────────────────────── */

export async function analyseChartImage(dataUrlOrBase64, {
  assetHint = "", tfHint = "", model = VISION_MODEL_DEFAULT,
  signal = null,
} = {}) {
  const puter = await waitForPuter();
  const b64 = typeof dataUrlOrBase64 === "string" && dataUrlOrBase64.startsWith("data:")
    ? dataUrlOrBase64
    : `data:image/jpeg;base64,${dataUrlOrBase64}`;
  const prompt = _visionPrompt(assetHint, tfHint);

  let resp;
  try {
    resp = await puter.ai.chat(prompt, b64, false, { model, signal });
  } catch (e) {
    if (e && (e.name === "AbortError" || e?.code === "aborted")) throw e;
    const msg = String(e?.message || e || "unknown puter error");
    // The first call per session often throws "user not authenticated" — surface
    // an actionable instruction instead of the raw stack.
    if (/not auth|login|sign/i.test(msg)) {
      throw new Error(
        "Puter sign-in required — click the Puter popup to authenticate. " +
        "AI runs on your own Puter wallet; no server key needed.");
    }
    throw new Error(`Puter AI call failed: ${msg}`);
  }

  const text = typeof resp === "string"
    ? resp
    : (resp?.message?.content || resp?.text || "");
  const parsed = _extractJson(text);
  if (!parsed) {
    // Return a minimal shape so the UI still renders meaningfully.
    return _normalise({
      direction: "NONE", confidence: 0,
      narrative: text ? text.slice(0, 800) : "Model returned no parseable JSON.",
      bullets: ["Model did not return valid JSON — try a tighter crop or a different model."],
      __model: model,
    });
  }
  parsed.__model = model;
  return _normalise(parsed);
}

export async function chatText(prompt, { model = "gpt-5.6-luna", signal = null } = {}) {
  const puter = await waitForPuter();
  const resp = await puter.ai.chat(prompt, { model, signal });
  if (typeof resp === "string") return resp;
  return resp?.message?.content || resp?.text || String(resp);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Iter 191 — Chartick text helpers (client-side Puter, no server key)
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Generate the 4-angle Deep Dive narrative client-side from the backend's
 * deterministic context block. Returns an array of `{name,summary,bullets,score}`
 * objects shaped exactly like the backend-rendered angles.
 */
export async function generateDeepDiveAngles({
  tech_summary, ensemble_summary, asset, timeframe,
  model = "gpt-5.6-luna", signal = null,
} = {}) {
  const prompt = [
    "You are a senior short-horizon markets analyst. Produce a 4-angle deep dive.",
    `Context:\n- ${tech_summary || `${asset} @ ${timeframe}`}\n- ${ensemble_summary || "ensemble unavailable"}`,
    "Angles:",
    "  1) trend     — direction / strength / duration / EMA stack",
    "  2) momentum  — RSI/MACD/Stoch state, divergences",
    "  3) structure — HH/LL, S/R zones, breakout potential",
    "  4) context   — volatility regime, time-of-day, risk-on/off",
    'Return STRICT JSON ONLY, no prose:',
    '{"angles":[{"name":"trend","summary":"<=180 chars","bullets":["..","..","..3-5"],"score":-5..5},',
    '{"name":"momentum",...},{"name":"structure",...},{"name":"context",...}],',
    '"composite_score":-20..20,"composite_direction":"BUY|SELL|NONE","composite_confidence":0..1}',
  ].join("\n");

  const puter = await waitForPuter();
  const resp = await puter.ai.chat(prompt, { model, signal });
  const text = typeof resp === "string"
    ? resp
    : (resp?.message?.content || resp?.text || "");
  const parsed = _extractJson(text);
  if (!parsed) return null;

  const num = (x, d = 0) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : d;
  };
  const angles = Array.isArray(parsed.angles) ? parsed.angles.map((a) => ({
    name: String(a?.name || "").toLowerCase(),
    summary: String(a?.summary || "").slice(0, 400),
    bullets: Array.isArray(a?.bullets) ? a.bullets.map(String).slice(0, 5) : [],
    score: Math.max(-5, Math.min(5, num(a?.score, 0))),
    llm: true,
  })) : [];
  const composite_score = Math.max(-20, Math.min(20, num(parsed.composite_score, 0)));
  const composite_direction = String(parsed.composite_direction || "").toUpperCase();
  let composite_confidence = num(parsed.composite_confidence, 0);
  if (composite_confidence > 1) composite_confidence /= 100;
  composite_confidence = Math.max(0, Math.min(1, composite_confidence));
  return {
    angles,
    composite_score,
    composite_direction: ["BUY", "SELL", "NONE"].includes(composite_direction)
      ? composite_direction : null,
    composite_confidence,
    model,
  };
}

/**
 * Generate the Daily Brief narrative client-side. Returns a plain string
 * (<=1500 chars) that the UI shows and PATCHes back to the backend.
 */
export async function generateBriefNarrative({
  watchlist_quotes = [], hot_movers = [], recent_alerts = [],
  model = "gpt-5.6-luna", signal = null,
} = {}) {
  const prompt = [
    "You are a concise trading-desk analyst. Write a 3-paragraph morning brief:",
    "  Paragraph 1 = watchlist overview.",
    "  Paragraph 2 = top hot movers.",
    "  Paragraph 3 = key risks and opportunities.",
    "Max 450 characters total. No disclaimers, no filler, no markdown.",
    `Watchlist quotes: ${JSON.stringify(watchlist_quotes).slice(0, 1500)}`,
    `Top hot movers: ${JSON.stringify(hot_movers).slice(0, 1500)}`,
    `Recent alert fires: ${JSON.stringify(recent_alerts).slice(0, 1500)}`,
  ].join("\n");

  const puter = await waitForPuter();
  const resp = await puter.ai.chat(prompt, { model, signal });
  const text = typeof resp === "string"
    ? resp
    : (resp?.message?.content || resp?.text || "");
  return String(text || "").trim().slice(0, 1500);
}

export const PUTER_VISION_MODELS = [
  { value: "gpt-5.6-luna",            label: "GPT-5.6 Luna (fast)" },
  { value: "gpt-6-astra",             label: "GPT-6 Astra (highest accuracy)" },
  { value: "claude-sonnet-5",         label: "Claude Sonnet 5" },
  { value: "gemini-3-pro",            label: "Gemini 3 Pro" },
  { value: "gemini-3.5-flash-lite",   label: "Gemini 3.5 Flash (cheap)" },
];
